import React from 'react';
import { usePostHog } from 'posthog-js/react';
import { getSubdomain } from '../utils/utils';
import { trackReactErrorBoundary } from '../utils/posthogEvents';

class ErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            hasError: false,
            error: null,
            errorInfo: null,
            errorId: null
        };
    }

    static getDerivedStateFromError(error) {
        return { hasError: true, error };
    }

    componentDidCatch(error, errorInfo) {
        const errorId = `react-boundary-${Date.now()}-${Math.random().toString(36).substring(2, 8)}`;
        this.setState({ errorInfo, errorId });

        const { posthog, colla } = this.props;
        trackReactErrorBoundary(posthog, colla, error, errorInfo, errorId);
    }

    handleReload = () => {
        window.location.reload();
    };

    render() {
        if (!this.state.hasError) {
            return this.props.children;
        }

        return (
            <div style={{
                minHeight: '100vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '32px',
                backgroundColor: '#f5f5f5',
                boxSizing: 'border-box'
            }}>
                <div style={{
                    maxWidth: '480px',
                    width: '100%',
                    backgroundColor: '#ffffff',
                    borderRadius: '12px',
                    padding: '32px',
                    boxShadow: '0 12px 32px rgba(0,0,0,0.08)',
                    textAlign: 'center'
                }}>
                    <div style={{ fontSize: '48px', marginBottom: '16px' }}>🤯</div>
                    <h1 style={{ fontSize: '24px', marginBottom: '12px' }}>Alguna cosa ha anat malament</h1>
                    <p style={{ color: '#444', marginBottom: '16px' }}>
                        No hem pogut carregar l'aplicació. Torna-ho a provar refrescant la pàgina.
                        Si el problema continua, contacta amb l'equip tècnic i facilita'ls-hi el codi d'error.
                    </p>
                    {this.state.errorId && (
                        <div style={{
                            backgroundColor: '#f0f4ff',
                            color: '#1a3c8f',
                            borderRadius: '8px',
                            padding: '12px 16px',
                            marginBottom: '16px',
                            wordBreak: 'break-word',
                            fontFamily: 'monospace'
                        }}>
                            Codi d'error: {this.state.errorId}
                        </div>
                    )}
                    {this.state.error && (
                        <details style={{ textAlign: 'left', marginBottom: '16px', color: '#666' }}>
                            <summary>Detalls tècnics</summary>
                            <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                {this.state.error?.message}
                            </pre>
                        </details>
                    )}
                    <button
                        onClick={this.handleReload}
                        style={{
                            padding: '12px 24px',
                            backgroundColor: '#007bff',
                            color: '#ffffff',
                            border: 'none',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            fontSize: '16px'
                        }}
                    >
                        Torna-ho a provar
                    </button>
                </div>
            </div>
        );
    }
}

class ErrorBoundaryWithTracking extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            posthog: null,
            colla: null,
            initialized: false
        };
    }

    componentDidMount() {
        try {
            // Safely try to get PostHog and subdomain
            import('posthog-js/react').then(({ usePostHog }) => {
                // Can't use hooks in class component, so we'll get posthog from window if available
                const posthog = window.posthog || null;
                const colla = getSubdomain();
                this.setState({ posthog, colla, initialized: true });
            }).catch(() => {
                // PostHog not available - continue without tracking
                const colla = getSubdomain();
                this.setState({ posthog: null, colla, initialized: true });
            });
        } catch (error) {
            // Continue without tracking
            const colla = getSubdomain();
            this.setState({ posthog: null, colla, initialized: true });
        }
    }

    render() {
        const { posthog, colla } = this.state;
        return (
            <ErrorBoundary posthog={posthog} colla={colla}>
                {this.props.children}
            </ErrorBoundary>
        );
    }
}

export default ErrorBoundaryWithTracking;
